<template>
  <div class="container-fluid page-card adminWeeducCreateVideo">
    <h3 class="page-title">{{ $t('admin.videoForm.titleNew') }}</h3>
    <p class="page-subtitle">
      {{ $t('admin.videoForm.subtitle') }}
    </p>
    <hr class="page-card__divider mb-4" />

    <div class="create-video-flow">
      <div
        class="steps-header"
        role="list"
        :aria-label="$t('admin.videoForm.stepsAria')"
      >
        <div
          v-for="(step, idx) in steps"
          :key="idx"
          :class="['step-indicator', { active: currentStep === idx + 1, done: currentStep > idx + 1 }]"
          role="listitem"
          :aria-current="currentStep === idx + 1 ? 'step' : undefined"
        >
          <span class="step-number">{{ idx + 1 }}</span>
          <span class="step-label">{{ step }}</span>
        </div>
      </div>

      <div class="steps-content">
        <!-- Passo 1: Origem -->
        <div v-if="currentStep === 1" class="step-panel">
          <div class="video-source-cards step1-options">
            <div
              class="source-card"
              :class="{ selected: originSource == 1 || originSource == '1' }"
              @click="handleSourceCardClick(1)"
            >
              <div class="icon">
                <fa icon="video" />
              </div>
              <div class="source-title">{{ $t('admin.videoForm.library') }}</div>
              <div class="source-desc">
                {{ $t('admin.videoForm.libraryDesc') }}
              </div>
              <input
                type="radio"
                v-model="videoData.source"
                value="1"
                class="hidden-radio"
              />
            </div>
            <div
              class="source-card"
              :class="{ selected: originSource == 2 || originSource == '2' }"
              @click="handleSourceCardClick(2)"
            >
              <div class="icon source-card-icon--youtube">
                <fa :icon="['fab', 'youtube']" />
              </div>
              <div class="source-title">{{ $t('admin.videoForm.youtube') }}</div>
              <div class="source-desc">
                {{ $t('admin.videoForm.youtubeDesc') }}
              </div>
              <input
                type="radio"
                v-model="videoData.source"
                value="2"
                class="hidden-radio"
              />
            </div>
            <div
              class="source-card"
              :class="{ selected: originSource == 3 || originSource == '3' }"
              @click="handleSourceCardClick(3)"
            >
              <div class="icon source-card-icon--vimeo">
                <fa :icon="['fab', 'vimeo']" />
              </div>
              <div class="source-title">{{ $t('admin.videoForm.vimeo') }}</div>
              <div class="source-desc">
                {{ $t('admin.videoForm.vimeoDesc') }}
              </div>
              <input
                type="radio"
                v-model="videoData.source"
                value="3"
                class="hidden-radio"
              />
            </div>
          </div>
        </div>

        <!-- Passo 2: Upload ou Link -->
        <div v-if="currentStep === 2" class="step-panel create-video-step2">
          <p class="create-video-flow__lead">
            {{ $t('admin.videoForm.stepUploadLead') }}
          </p>

          <div
            v-if="videoData.source == 1 || videoData.source == '1'"
            class="create-video-step2__choices video-source-cards"
          >
            <button
              type="button"
              class="source-card"
              :class="{ selected: !!reuseVideoData.id }"
              @click="openReuseModal"
            >
              <div class="icon">
                <fa icon="sync" />
              </div>
              <div class="source-title">{{ $t('admin.videoForm.reuseVideo') }}</div>
              <div class="source-desc">{{ $t('admin.videoForm.reuseVideoHint') }}</div>
            </button>
            <button
              type="button"
              class="source-card"
              :class="{ selected: showUploadVideo }"
              @click="chooseUpload"
            >
              <div class="icon">
                <fa icon="upload" />
              </div>
              <div class="source-title">{{ $t('admin.videoForm.uploadFile') }}</div>
              <div class="source-desc">{{ $t('admin.videoForm.uploadFileHint') }}</div>
            </button>
          </div>
          <transition name="fade">
            <div
              class="form-group create-video-step2__dropzone-wrap"
              v-if="showUploadVideo && !reuseVideoData.id"
            >
              <div class="form-file create-video-upload">
                <div class="file-wrapper primary create-video-step2__dropzone">
                  <label
                    class="thumbnail thumb-img create-video-dropzone__surface"
                    for="weeduc-create-video-upload"
                  >
                    <span class="create-video-dropzone__icon-wrap" aria-hidden="true">
                      <svg
                        class="uploadVideoIcon create-video-dropzone__icon"
                        xmlns="http://www.w3.org/2000/svg"
                        viewBox="0 0 576 512"
                      >
                        <path
                          d="M0 128C0 92.7 28.7 64 64 64l256 0c35.3 0 64 28.7 64 64l0 256c0 35.3-28.7 64-64 64L64 448c-35.3 0-64-28.7-64-64L0 128zM559.1 99.8c10.4 5.6 16.9 16.4 16.9 28.2l0 256c0 11.8-6.5 22.6-16.9 28.2s-23 5-32.9-1.6l-96-64L416 337.1l0-17.1 0-128 0-17.1 14.2-9.5 96-64c9.8-6.5 22.4-7.2 32.9-1.6z"
                        />
                      </svg>
                    </span>
                    <span class="create-video-dropzone__title">{{ $t('admin.videoForm.uploadVideo') }}</span>
                    <span class="create-video-dropzone__hint">
                      Arraste e solte o arquivo aqui ou clique para selecionar
                    </span>
                  </label>
                  <input
                    id="weeduc-create-video-upload"
                    class="form-control create-video-dropzone__input"
                    multiple
                    type="file"
                    @change="uploadVideoAws"
                    accept=".mp4, .avi"
                  />
                </div>
                <p class="create-video-dropzone__formats" role="note">
                  Formatos aceitos:
                  <strong>.mp4</strong>
                  e
                  <strong>.avi</strong>
                </p>
              </div>
              <div
                class="upload-status"
                :class="{ 'upload-status--done': isUploaded }"
                v-if="isUploading || isUploaded"
              >
                <div class="upload-status__head">
                  <div
                    class="loading-spinner"
                    v-if="isUploading && !isUploaded"
                  >
                    <fa icon="fa-sync fa-spin" />
                    <span>{{ $t('admin.videoForm.uploading') }}</span>
                  </div>
                  <div
                    class="upload-complete"
                    v-else-if="isUploaded"
                  >
                    <fa icon="fa-check-circle" />
                    <span>{{ $t('admin.videoForm.uploadSuccess') }}</span>
                  </div>
                  <span class="upload-status__percent">
                    {{ Math.round(progress) }}%
                  </span>
                </div>
                <div
                  class="progress-bar"
                  role="progressbar"
                  :aria-valuenow="Math.round(progress)"
                  aria-valuemin="0"
                  aria-valuemax="100"
                  :aria-label="$t('admin.videoForm.uploading')"
                >
                  <div
                    class="progress-fill"
                    :style="{ width: Math.min(100, Math.max(0, progress)) + '%' }"
                  />
                </div>
              </div>
            </div>
          </transition>
          <div
            v-if="videoData.source == 2 || videoData.source == '2' || videoData.source == 3 || videoData.source == '3'"
            class="form-group create-video-step2__link-field"
          >
            <label class="labeld required" for="videoExternalUrl">
              Link do vídeo no
              {{
                videoData.source == 2 || videoData.source == '2'
                  ? 'Youtube'
                  : 'Vimeo'
              }}
            </label>
            <input
              id="videoExternalUrl"
              type="text"
              class="inputd create-video-step2__url-input"
              v-model="videoData.youtubeUrl"
              :placeholder="$t('admin.videoForm.urlPlaceholder')"
              required
              @input="updateVideoPreview"
            />
          </div>

          <footer class="step-actions create-video-step2__footer">
            <button type="button" class="btn-weflix default" @click="prevStep">
              Voltar
            </button>
            <button
              type="button"
              class="btn-weflix primary default"
              :disabled="!canGoToStep3Upload"
              @click="nextStep"
            >
              Próximo
            </button>
          </footer>
        </div>

        <!-- Passo 3: Informações e Preview -->
        <div v-if="currentStep === 3" class="step-panel step-panel-step3">
          <div class="step3-columns">
            <!-- Coluna 1: Formulário -->
            <div class="step3-form-col step3-form-col-60">
              <div class="form-group">
                <label class="labeld required" for="videoTitle">{{ $t('common.title') }}</label>
                  <input
                  id="videoTitle"
                    type="text"
                    class="inputd"
                    v-model="videoData.title"
                    :placeholder="$t('admin.videoForm.titlePlaceholder')"
                    required
                  />
                  <small>{{ $t('admin.videoForm.titleHint') }}</small>
                </div>
              <div class="form-group">
                <label class="labeld" for="videoDescription">{{ $t('common.description') }}</label>
                  <textarea
                  id="videoDescription"
                    class="inputd"
                    v-model="videoData.description"
                    :placeholder="$t('admin.videoForm.descriptionPlaceholder')"
                    rows="3"
                  ></textarea>
                  <small>{{ $t('admin.videoForm.descriptionHint') }}</small>
              </div>
            
            </div>
            <!-- Coluna 2: Preview -->
            <div class="step3-preview-col step3-preview-col-40">
              <div class="preview-container step3-preview">
                <div
                  class="video-preview step3-preview-frame"
                  v-if="
                    (videoData.youtubeUrl && (videoData.source == 2 || videoData.source == '2' || videoData.source == 3 || videoData.source == '3')) ||
                    (videoData.path && (videoData.source == 1 || videoData.source == '1')) ||
                    (reuseVideoData.id && toHttpUrl(videoPlayer))
                  "
                >
                  <iframe
                    v-if="videoData.source == 2 || videoData.source == '2'"
                    :src="getYoutubeEmbedUrl(videoData.youtubeUrl)"
                    frameborder="0"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowfullscreen
                  ></iframe>
                  <iframe
                    v-if="videoData.source == 3 || videoData.source == '3'"
                    :src="getVimeoEmbedUrl(videoData.youtubeUrl)"
                    frameborder="0"
                    allow="autoplay; fullscreen; picture-in-picture"
                    allowfullscreen
                  ></iframe>
                  <video
                    v-if="(videoData.source == 1 || videoData.source == '1') && videoData.path && !reuseVideoData.id"
                    controls
                  >
                    <source :src="videoData.path" type="video/mp4" />
                    Seu navegador não suporta o elemento de vídeo.
                  </video>
                  <iframe
                    v-if="(videoData.source == 1 || videoData.source == '1') && reuseVideoData.id && toHttpUrl(videoPlayer)"
                    :src="toHttpUrl(videoPlayer)"
                    frameborder="0"
                    allow="autoplay; fullscreen"
                    allowfullscreen
                  ></iframe>
                </div>
                <div class="video-placeholder step3-preview-frame" v-else>
                  <p>{{ $t('admin.videoForm.addVideoPreview') }}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <footer
        v-if="currentStep === 3"
        class="create-video-step3__footer"
      >
        <button
          type="button"
          class="btn-weflix default"
          @click="prevStep"
        >
          Voltar
        </button>
        <button
          type="button"
          class="btn-weflix primary default"
          :disabled="isSaving"
          @click="saveVideo"
        >
          Salvar vídeo
        </button>
      </footer>
    </div>

    <!-- Modal: reutilizar vídeo da videoteca -->
    <v-dialog v-model="showReuseModal" max-width="720" class="reuse-video-dialog">
      <v-card class="reuse-video-dialog__card" variant="flat">
        <header class="reuse-video-dialog__header">
          <h2 id="reuse-video-dialog-title" class="reuse-video-dialog__title">
            Reutilizar vídeo
          </h2>
          <p class="reuse-video-dialog__lead">
            Escolha um curso e uma atividade para pré-visualizar o vídeo antes
            de confirmar.
          </p>
        </header>

        <div class="reuse-video-dialog__body">
          <div class="reuse-video-dialog__grid">
            <div class="reuse-video-dialog__field">
              <label class="reuse-video-dialog__label" for="reuse-video-course">
                Curso
              </label>
              <select
                id="reuse-video-course"
                v-model="selectedCourse"
                class="form-control reuse-video-dialog__select"
                @change="loadLessons"
                :disabled="loadingLessons"
              >
                <option value="">Selecione um curso</option>
                <option
                  v-for="collection in collections"
                  :key="collection.id"
                  :value="collection.id"
                >
                  {{ collection.name }}
                </option>
              </select>
              <div
                v-if="loadingLessons"
                class="reuse-video-dialog__loading"
                role="status"
                aria-live="polite"
              >
                <fa icon="fa-sync fa-spin" />
                <span>Carregando atividades…</span>
              </div>
            </div>

            <div class="reuse-video-dialog__field">
              <label class="reuse-video-dialog__label" for="reuse-video-lesson">
                Atividade
              </label>
              <select
                id="reuse-video-lesson"
                v-model="selectedLesson"
                class="form-control reuse-video-dialog__select"
                @change="getVideo"
                :disabled="activitySelectDisabled"
              >
                <option value="">Selecione uma atividade</option>
                <option
                  v-for="lesson in listLessons"
                  :key="lesson.id"
                  :value="lesson.id"
                >
                  {{ lesson.name }}
                </option>
              </select>
              <div
                v-if="loadingLessons"
                class="reuse-video-dialog__loading"
                role="status"
                aria-live="polite"
              >
                <fa icon="fa-sync fa-spin" />
                <span>{{ $t('admin.videoForm.loadingVideo') }}</span>
              </div>
              <p
                v-else-if="showNoVideoLessons"
                class="reuse-video-dialog__hint"
                role="status"
              >
                {{ $t('admin.videoForm.noVideoActivities') }}
              </p>
              <div
                v-if="loadingVideo"
                class="reuse-video-dialog__loading"
                role="status"
                aria-live="polite"
              >
                <fa icon="fa-sync fa-spin" />
                <span>{{ $t('admin.videoForm.loadingVideo') }}</span>
              </div>
            </div>
          </div>

          <section
            class="reuse-video-dialog__preview"
            aria-labelledby="reuse-video-dialog-title"
          >
            <div v-if="loadingVideo" class="reuse-video-dialog__empty">
              <p class="reuse-video-dialog__empty-text">
                {{ $t('admin.videoForm.loadingVideo') }}
              </p>
            </div>
            <div
              v-else-if="previewEmbedUrl"
              class="reuse-video-dialog__preview-card"
            >
              <div class="reuse-video-dialog__frame">
                <iframe
                  :key="previewEmbedUrl"
                  :src="previewEmbedUrl"
                  :title="$t('admin.videoForm.previewTitle')"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; fullscreen"
                  allowfullscreen
                />
              </div>
              <div class="reuse-video-dialog__meta">
                <p
                  v-if="reusePreviewTypeLabel"
                  class="reuse-video-dialog__type"
                >
                  {{ reusePreviewTypeLabel }}
                </p>
                <h3 class="reuse-video-dialog__meta-title">
                  {{ videoInfoCopy.title }}
                </h3>
                <p
                  v-if="videoInfoCopy.description"
                  class="reuse-video-dialog__meta-desc"
                >
                  {{ videoInfoCopy.description }}
                </p>
              </div>
            </div>
            <div v-else class="reuse-video-dialog__empty">
              <p class="reuse-video-dialog__empty-text">
                Selecione um curso e uma atividade para visualizar o vídeo.
              </p>
            </div>
          </section>
        </div>

        <footer class="reuse-video-dialog__footer">
          <button
            type="button"
            class="reuse-video-dialog__btn reuse-video-dialog__btn--secondary"
            @click="closeReuseModal"
          >
            Cancelar
          </button>
          <button
            type="button"
            class="reuse-video-dialog__btn reuse-video-dialog__btn--primary"
            :disabled="!videoInfoCopy.title"
            @click="reuseVideo"
          >
            Selecionar vídeo
          </button>
        </footer>
      </v-card>
    </v-dialog>

    <Loader v-if="isSaving" />

  
  </div>
</template>
<script>
import { mapState } from 'vuex';
import apiLesson from '@/utils/lesson';
import Loader from '@/components/Loader/loader.vue';
import Videos from '@/utils/Weflix/videos';
import apiAWS from '@/utils/storage/aws';
import videoteca from '@/utils/videoteca';
import { normalizePlayerUrl } from '@/utils/storage/playerVideo';
import companyApi from '@/utils/company';
import { toast } from 'vue3-toastify';

function emptyVideotecaUpload() {
  return {
    fileId: null,
    callbackUrl: null,
    uploadId: null,
    partUrls: [],
    partSize: 0,
    partCount: 0,
    fileUploadUrl: null,
    thumbnailUploadUrl: null,
    file: null,
    filename: null,
    mediaType: null,
    size: 0,
    parts: [],
    thumbnailUploaded: false,
  };
}

export default {
  components: {
    Loader,
  },
  data() {
    return {
      videoData: {
        source: null,
        path: '',
        title: '',
        description: '',
        youtubeUrl: '',
        token: '',
        uploadId: '',
      },
      showHelp: false,
      isSaving: false,
      isUploading: false,
      isUploaded: false,
      progress: 0,
      tokenVideo: null,
      videotecaUpload: emptyVideotecaUpload(),
      videotecaFolderId: null,
      previewCoverVideo: null,
      coverVideo: null,
      loaderImage: false,
      showReuseModal: false,
      existingVideos: [],
      loading: false,
      loadingLessons: false,
      loadingVideo: false,
      showUploadVideo: false,
      listLessons: [],
      selectedCourse: '',
      selectedLesson: '',
      courses: [],
      collections: [],
      videoInfoCopy: {},
      videoPlayer: '',
      previewEmbedUrl: '',
      reuseVideoData: {},
      tokenVideoReuse: '',
      currentStep: 1,
      /** Origem escolhida no passo 1 (1 videoteca, 2 YouTube, 3 Vimeo). */
      originSource: null,
    };
  },
  computed: {
    steps() {
      return [
        this.$t('admin.videoForm.stepOrigin'),
        this.$t('admin.videoForm.stepUploadLink'),
        this.$t('admin.videoForm.stepInfo'),
      ];
    },
    ...mapState(['token', 'currentCollections', 'currentModule']),
    canGoToStep3() {
      if (this.videoData.source == 1 || this.videoData.source == '1') {
        return !!this.videoData.path || !!this.reuseVideoData.id;
      }
      if (
        this.videoData.source == 2 ||
        this.videoData.source == '2' ||
        this.videoData.source == 3 ||
        this.videoData.source == '3'
      ) {
        return !!this.videoData.youtubeUrl;
      }
      return false;
    },
    canGoToStep3Upload() {
      // Se for upload (videoteca), só habilita se upload finalizado
      if (this.videoData.source == 1 || this.videoData.source == '1') {
        // Se está mostrando upload, só habilita se isUploaded
        if (this.showUploadVideo) {
          return this.isUploaded;
        }
        // Se não está mostrando upload, pode ser reutilizar vídeo, então libera se tem path ou vídeo reutilizado
        return !!this.videoData.path || !!this.reuseVideoData.id;
      }
      // Para YouTube/Vimeo, segue regra normal
      if (
        this.videoData.source == 2 ||
        this.videoData.source == '2' ||
        this.videoData.source == 3 ||
        this.videoData.source == '3'
      ) {
        return !!this.videoData.youtubeUrl;
      }
      return false;
    },
    reusePreviewTypeLabel() {
      const type = Number(this.videoInfoCopy?.type);
      if (type === 1) return this.$t('admin.videoForm.library');
      if (type === 2) return this.$t('admin.videoForm.youtube');
      if (type === 3) return this.$t('admin.videoForm.vimeo');
      return '';
    },
    hasVideoLessons() {
      return Array.isArray(this.listLessons) && this.listLessons.length > 0;
    },
    activitySelectDisabled() {
      return (
        !this.selectedCourse ||
        this.loadingLessons ||
        this.loadingVideo ||
        !this.hasVideoLessons
      );
    },
    showNoVideoLessons() {
      return Boolean(
        this.selectedCourse && !this.loadingLessons && !this.hasVideoLessons
      );
    },
  },
  watch: {
    showReuseModal(isOpen) {
      if (!isOpen) {
        this.handleReuseModalClose();
      }
    },
  },
  methods: {
    isLibraryOrigin() {
      return (
        this.originSource == 1 ||
        this.originSource == '1' ||
        !!this.reuseVideoData.id
      );
    },
    prevStep() {
      if (this.currentStep === 3 && this.isLibraryOrigin()) {
        this.currentStep = 1;
        return;
      }
      if (this.currentStep > 1) this.currentStep--;
    },
    nextStep() {
      if (this.currentStep < 3) this.currentStep++;
    },
    chooseUpload() {
      this.reuseVideoData = {};
      this.videoInfoCopy = {};
      this.videoPlayer = '';
      this.previewEmbedUrl = '';
      this.videotecaUpload = emptyVideotecaUpload();
      this.tokenVideo = null;
      this.isUploaded = false;
      this.isUploading = false;
      this.progress = 0;
      this.showUploadVideo = true;
    },
    // Novo método para avançar ao próximo passo ao clicar no card
    handleSourceCardClick(source) {
      this.selectSource(source);
      this.nextStep();
    },
    selectSource(source) {
      // Aceita tanto número quanto string
      this.originSource = source;
      this.videoData.source = source;
      // Limpa campos ao trocar de origem
      this.videoData.youtubeUrl = '';
      this.videoData.path = '';
      this.showUploadVideo = false;
      this.isUploaded = false;
      this.isUploading = false;
      this.progress = 0;
      this.videotecaUpload = emptyVideotecaUpload();
      this.tokenVideo = null;
      this.coverVideo = null;
      this.previewCoverVideo = null;
      // Limpa dados de reutilização
      this.reuseVideoData = {};
      this.videoInfoCopy = {};
      this.videoPlayer = '';
      this.previewEmbedUrl = '';
      this.selectedCourse = '';
      this.selectedLesson = '';
    },
    async openReuseModal() {
      this.resetReusePreview();
      this.showReuseModal = true;
    },
    closeReuseModal() {
      this.showReuseModal = false;
    },
    resetReusePreview() {
      this.selectedCourse = '';
      this.selectedLesson = '';
      this.listLessons = [];
      this.videoInfoCopy = {};
      this.videoPlayer = '';
      this.previewEmbedUrl = '';
      this.loadingVideo = false;
      this.loadingLessons = false;
    },
    handleReuseModalClose() {
      if (this.currentStep === 3 && this.reuseVideoData.id) {
        this.selectedCourse = '';
        this.selectedLesson = '';
        this.listLessons = [];
        return;
      }
      this.resetReusePreview();
      this.reuseVideoData = {};
    },
    async loadLessons() {
      if (this.selectedCourse) {
        this.loadingLessons = true;
        this.listLessons = [];
        this.selectedLesson = '';
        this.videoInfoCopy = {};
        this.videoPlayer = '';
        this.previewEmbedUrl = '';
        
        try {
          const response = await apiLesson.getLeistLessonByCourse(
            this.selectedCourse,
            this.token,
            0
          );
          this.listLessons = Array.isArray(response) ? response : [];
        } catch (error) {
          toast.error(this.$t('admin.videoForm.loadActivitiesError'));
          console.error('Erro ao carregar atividades:', error);
        } finally {
          this.loadingLessons = false;
        }
      }
    },
    async selectExistingVideo(video) {},
    toggleHelp() {
      this.showHelp = !this.showHelp;
    },
    getYoutubeEmbedUrl(url) {
      const videoId = this.extractYoutubeId(url);
      return videoId ? `https://www.youtube.com/embed/${videoId}` : '';
    },
    extractYoutubeId(url) {
      if (!url || typeof url !== 'string') return '';
      try {
        const urlObj = new URL(url);
        const fromQuery = urlObj.searchParams.get('v');
        if (fromQuery) return fromQuery;

        const parts = urlObj.pathname.split('/').filter(Boolean);
        if (urlObj.hostname.includes('youtu.be')) return parts[0] || '';

        const embedIndex = parts.indexOf('embed');
        if (embedIndex >= 0) return parts[embedIndex + 1] || '';

        const shortsIndex = parts.indexOf('shorts');
        if (shortsIndex >= 0) return parts[shortsIndex + 1] || '';

        return '';
      } catch (error) {
        console.error('URL inválida:', error);
        return '';
      }
    },
    getVimeoEmbedUrl(url) {
      if (!url) return '';
      const videoId = String(url).split('/').pop();
      return videoId
        ? `https://player.vimeo.com/video/${videoId}`
        : '';
    },
    lessonSourceUrl(lesson) {
      if (!lesson) return '';
      return lesson.youtubeUrl || lesson.path || '';
    },
    isVideotecaType(type) {
      return Number(type) === 1;
    },
    isYoutubeType(type) {
      return Number(type) === 2;
    },
    isVimeoType(type) {
      return Number(type) === 3;
    },
    toHttpUrl(value) {
      return normalizePlayerUrl(value);
    },
    async getVideo() {
      if (!this.selectedLesson) {
        this.videoInfoCopy = {};
        this.videoPlayer = '';
        this.previewEmbedUrl = '';
        return;
      }

      this.loadingVideo = true;
      this.videoPlayer = '';
      this.previewEmbedUrl = '';

      try {
        const response = await Videos.getVideosById(
          this.token,
          this.selectedLesson
        );
        this.videoInfoCopy = response || {};

        const type = Number(response?.type);
        const sourceUrl = this.lessonSourceUrl(response);

        if (type === 2) {
          this.previewEmbedUrl = this.getYoutubeEmbedUrl(sourceUrl);
          return;
        }

        if (type === 3) {
          this.previewEmbedUrl = this.getVimeoEmbedUrl(sourceUrl);
          return;
        }

        if (type === 1) {
          await this.getPlayerVideo();
          this.previewEmbedUrl = this.toHttpUrl(this.videoPlayer);
        }
      } catch (error) {
        console.error('Erro ao obter dados do video:', error);
        toast.error(this.$t('admin.videoForm.loadError'));
        this.videoInfoCopy = {};
        this.videoPlayer = '';
        this.previewEmbedUrl = '';
      } finally {
        this.loadingVideo = false;
      }
    },
    async getPlayerVideo() {
      if (!this.selectedLesson) return;
      if (Number(this.videoInfoCopy?.type) !== 1) return;

      try {
        this.videoPlayer = await videoteca.getFrameVideoteca(
          this.token,
          this.selectedLesson
        );
      } catch (error) {
        console.error('Erro ao obter o player:', error);
        this.videoPlayer = '';
      }
    },
    reuseVideo() {
      if (!this.videoInfoCopy.id) {
        toast.error(this.$t('admin.videoForm.noVideoSelected'));
        return;
      }

      this.reuseVideoData = { ...this.videoInfoCopy };

      const type = Number(this.reuseVideoData.type);
      const sourceUrl = this.lessonSourceUrl(this.reuseVideoData);

      this.videoData.source = type;
      this.videoData.title = this.reuseVideoData.title || '';
      this.videoData.description = this.reuseVideoData.description || '';
      this.videoData.token = this.reuseVideoData.token || '';
      this.videoData.uploadId = this.reuseVideoData.uploadId || '';

      if (this.isYoutubeType(type) || this.isVimeoType(type)) {
        this.videoData.youtubeUrl = sourceUrl;
        this.videoData.path = '';
      } else {
        this.videoData.path = this.reuseVideoData.path || '';
        this.videoData.youtubeUrl = '';
      }

      this.showUploadVideo = false;
      this.isUploaded = false;
      this.isUploading = false;
      this.progress = 0;

      this.currentStep = 3;
      this.showReuseModal = false;
    },
    updateVideoPreview() {
      this.videoData.path = this.videoData.youtubeUrl;
    },
    externalVideoUrl() {
      return String(
        this.videoData.youtubeUrl
        || this.videoData.path
        || this.reuseVideoData?.youtubeUrl
        || this.reuseVideoData?.path
        || ''
      ).trim();
    },
    /** URL que o player do aluno já sabe incorporar (watch?v= e vimeo.com/id). */
    canonicalExternalPath(type, url) {
      if (Number(type) === 2) {
        const id = this.extractYoutubeId(url);
        return id ? `https://www.youtube.com/watch?v=${id}` : '';
      }
      if (Number(type) === 3) {
        const match = String(url).match(/vimeo\.com\/(?:video\/)?(\d+)/i);
        return match ? `https://vimeo.com/${match[1]}` : '';
      }
      return '';
    },
    async saveExternalVideo(type, title) {
      const path = this.canonicalExternalPath(type, this.externalVideoUrl());
      if (!path) {
        toast.warning(this.$t('admin.videoForm.invalidUrl'));
        return false;
      }

      await apiLesson.createNewLesson(
        {
          type,
          title,
          path,
          description: this.videoData.description || '',
          idQuiz: null,
          idModule: this.currentModule?.id,
          idCourse: this.currentCollections?.id || null,
          status: 1,
          sample: 0,
          videoThumbnail: this.coverVideo,
          videoTime: 0,
        },
        this.token
      );
      return true;
    },
    async saveVideo() {
      if (this.isSaving) {
        return;
      }

      const title = (this.videoData.title || '').trim();
      if (!title) {
        toast.warning(this.$t('admin.videoForm.fillRequiredToSave'));
        this.$nextTick(() => {
          const el = document.getElementById('videoTitle');
          if (el) {
            el.focus();
          }
        });
        return;
      }

      const sourceType = Number(this.videoData.source);
      this.isSaving = true;

      try {
        if (sourceType === 2 || sourceType === 3) {
          const saved = await this.saveExternalVideo(sourceType, title);
          if (!saved) {
            return;
          }
          this.$router.push({
            path: '/dashboard/curso',
            query: { toast: this.$t('admin.videoForm.createSuccess') },
          });
          return;
        }
      } catch (error) {
        toast.error(this.$t('admin.videoForm.saveError'));
        console.error('Erro ao salvar vídeo externo', error);
        return;
      } finally {
        if (sourceType === 2 || sourceType === 3) {
          this.isSaving = false;
        }
      }

      // Preparar payload baseado no tipo de vídeo
      let payload = {
        idModule: this.currentModule.id,
        title: this.videoData.title,
        description: this.videoData.description,
        videoThumbnail: this.coverVideo,
        sample: 0,
        type: this.videoData.source,
        status: 1,
        lessontime: 0,
      };

      // Se for vídeo reutilizado
      if (this.reuseVideoData.id) {
        payload.token = this.reuseVideoData.token ? [this.reuseVideoData.token] : null;
        payload.uploadId = this.reuseVideoData.uploadId;
        payload.path = this.reuseVideoData.path;
        payload.youtubeUrl = this.videoData.youtubeUrl || this.reuseVideoData.youtubeUrl;
      } else if (
        Number(this.videoData.source) === 1
        && this.videotecaUpload?.fileId
      ) {
        // Upload Videoteca v1
        const upload = this.videotecaUpload;
        const uploadId = upload.uploadId || null;
        const parts = Array.isArray(upload.parts) && upload.parts.length
          ? upload.parts
          : null;

        payload.fileId = upload.fileId;
        payload.callbackUrl = upload.callbackUrl;
        payload.filename = upload.filename;
        payload.mediaType = upload.mediaType;
        payload.size = upload.size;
        payload.thumbnailUploaded = Boolean(upload.thumbnailUploaded);
        payload.path = null;
        payload.youtubeUrl = null;

        // Backend: se houver parts, uploadId é obrigatório (multipart).
        // Single PUT (só fileUploadUrl) finaliza só com fileId + callbackUrl.
        if (uploadId && parts?.length) {
          payload.uploadId = uploadId;
          payload.parts = parts;
        }
      } else {
        // Videoteca reutilizada ou legado sem fileId. YouTube (2) e Vimeo (3) saem antes, em POST /lesson.
        payload.token = this.tokenVideo
          ? this.tokenVideo.urls.map((item) => item.token)
          : null;
        payload.uploadId = this.tokenVideo ? this.tokenVideo.uploadId : null;
        payload.path = this.videoData.path;
        payload.youtubeUrl = this.videoData.youtubeUrl;
      }

      try {
        await apiAWS.createVideoVideoteca(
          this.token,
          payload
        );
        this.isSaving = false;
        this.$router.push({ path: '/dashboard/curso', query: { toast: this.$t('admin.videoForm.createSuccess') } });
      } catch (error) {
        toast.error('Erro ao enviar arquivo para AWS. Por favor, tente novamente.');
        console.error('Erro ao enviar arquivo para AWS', error);
        this.isSaving = false;
      }
    },
    async putFileAws(url, file) {
      try {
        await apiAWS.putFileAWS(url, file);
      } catch (error) {

        console.error('Erro ao enviar arquivo para AWS');
        throw error;
      }
    },
    async updateCoverVideo(event) {
      this.loaderImage = false;
      const file = event.target.files[0];

      const validTypes = [
        'image/png',
        'image/jpeg',
        'image/jpg',
        'image/webp',
      ];
      if (!validTypes.includes(file.type)) {
        alert(this.$t('admin.videoForm.invalidImageFormat'));
        return;
      }

      this.previewCoverVideo = URL.createObjectURL(file);

      try {
        const thumbUrl = this.videotecaUpload?.thumbnailUploadUrl;
        if (thumbUrl) {
          await this.putFileAws(thumbUrl, file);
          this.videotecaUpload.thumbnailUploaded = true;
          this.coverVideo = null;
          this.loaderImage = true;
          return;
        }

        const response = await apiAWS.getSignatureImage(
          this.token,
          file.name
        );
        await this.putFileAws(response.url, file);
        this.coverVideo = response.token;
        this.loaderImage = true;
      } catch (error) {
        console.error('Erro na geração do TOKEN AWS');
        toast.error(this.$t('admin.videoForm.awsTokenError'));
      }
    },
    async ensureVideotecaFolderId() {
      if (this.videotecaFolderId) {
        return this.videotecaFolderId;
      }
      const status = await companyApi.getVideoteca(this.token);
      const folderId =
        status?.folderId
        || status?.idFolder
        || status?.idFolderVideoteca
        || null;
      if (!folderId) {
        throw new Error('VIDEOTECA_FOLDER_MISSING');
      }
      this.videotecaFolderId = String(folderId);
      return this.videotecaFolderId;
    },
    async uploadVideoAws(event) {
      try {
        const file = event.target.files[0];

        if (!file) {
          return;
        }

        const fileSizeMb = Math.round(file.size / (1024 * 1024));

        if (fileSizeMb > 4096) {
          alert(
            this.$t('admin.videoForm.fileTooLarge')
          );
          return;
        }

        this.videoData.path = URL.createObjectURL(file);
        this.videotecaUpload = emptyVideotecaUpload();
        this.isUploaded = false;
        this.progress = 0;

        let folderId;
        try {
          folderId = await this.ensureVideotecaFolderId();
        } catch (folderError) {
          if (folderError?.message === 'VIDEOTECA_FOLDER_MISSING') {
            toast.error(this.$t('admin.videoForm.videotecaFolderMissing'));
            return;
          }
          throw folderError;
        }

        const title =
          (this.videoData.title || '').trim()
          || file.name.replace(/\.[^.]+$/, '')
          || file.name;

        const initResponse = await apiAWS.initVideotecaUpload(this.token, {
          filename: file.name,
          mediaType: file.type || 'video/mp4',
          folderId,
          title,
          size: file.size,
          metadata: {},
        });

        if (!initResponse) {
          throw new Error(this.$t('admin.videoForm.invalidUploadUrls'));
        }

        const data = initResponse.data || initResponse;
        const multipart = data.multipart || null;
        const partUrls = Array.isArray(multipart?.partUrls)
          ? [...multipart.partUrls].sort(
            (a, b) => Number(a.partNumber) - Number(b.partNumber),
          )
          : [];
        const uploadId =
          multipart?.uploadId
          || data.uploadId
          || initResponse.uploadId
          || null;

        if (partUrls.length > 0 && !uploadId) {
          throw new Error(
            'Init multipart sem uploadId — não é possível finalizar o upload.',
          );
        }

        this.videotecaUpload = {
          ...emptyVideotecaUpload(),
          fileId: data.fileId || null,
          callbackUrl: data.callbackUrl || null,
          uploadId,
          partUrls,
          partSize: Number(multipart?.partSize) || 0,
          partCount: Number(multipart?.partCount) || partUrls.length || 0,
          fileUploadUrl: data.fileUploadUrl || null,
          thumbnailUploadUrl: data.thumbnailUploadUrl || null,
          file,
          filename: file.name,
          mediaType: file.type || 'video/mp4',
          size: file.size,
          parts: [],
          thumbnailUploaded: false,
        };

        // Compat: alguns fluxos ainda leem tokenVideo
        this.tokenVideo = {
          uploadId: this.videotecaUpload.uploadId,
          urls: partUrls.map((part) => ({
            url: part.url,
            token: null,
            partNumber: part.partNumber,
          })),
        };

        await this.putVideoAwsV1(this.videotecaUpload);
      } catch (error) {
        console.error('Erro ao enviar arquivo para AWS', error);
        alert(
          this.$t('admin.videoForm.uploadError')
        );
        this.isUploading = false;
        this.isUploaded = false;
        this.progress = 0;
        this.videotecaUpload = emptyVideotecaUpload();
      }
    },
    async putVideoAwsV1(uploadState) {
      this.isUploading = true;
      this.isUploaded = false;

      const file = uploadState.file;
      if (!file) {
        throw new Error(this.$t('admin.videoForm.invalidUploadUrls'));
      }

      const partUrls = uploadState.partUrls || [];
      const parts = [];

      // Multipart: PUT em cada partUrl
      if (partUrls.length > 0) {
        const partSize = Number(uploadState.partSize) || Math.ceil(file.size / partUrls.length);
        const totalChunks = partUrls.length;
        const progressArray = new Array(totalChunks).fill(0);

        for (let i = 0; i < totalChunks; i += 1) {
          const part = partUrls[i];
          const partNumber = Number(part.partNumber) || (i + 1);
          const start = i * partSize;
          const end = Math.min(start + partSize, file.size);
          const chunk = file.slice(start, end);

          try {
            const response = await apiAWS.putVideoAWS(
              part.url,
              chunk,
              (progress) => {
                progressArray[i] = progress;
                this.progress =
                  progressArray.reduce((a, b) => a + b, 0) / totalChunks;
              },
            );

            if (!response?.etag) {
              throw new Error(`ETag ausente na parte ${partNumber}`);
            }

            parts.push({
              partNumber,
              etag: response.etag,
            });
          } catch (error) {
            console.error(`Erro no upload da parte ${partNumber}:`, error);
            this.isUploading = false;
            this.isUploaded = false;
            this.progress = 0;
            alert(this.$t('admin.videoForm.uploadProgressError'));
            throw error;
          }
        }
      } else if (uploadState.fileUploadUrl) {
        // Single PUT — finalize só com fileId/callbackUrl (sem parts/uploadId)
        try {
          await apiAWS.putVideoAWS(
            uploadState.fileUploadUrl,
            file,
            (progress) => {
              this.progress = progress;
            },
          );
        } catch (error) {
          console.error('Erro no upload single-part:', error);
          this.isUploading = false;
          this.isUploaded = false;
          this.progress = 0;
          alert(this.$t('admin.videoForm.uploadProgressError'));
          throw error;
        }
      } else {
        throw new Error(this.$t('admin.videoForm.invalidUploadUrls'));
      }

      this.videotecaUpload = {
        ...this.videotecaUpload,
        parts: uploadState.uploadId ? parts : [],
      };
      this.isUploaded = true;
      this.isUploading = false;
      this.progress = 100;
    },
    async getCollections() {
      try {
        const response = await Videos.listCollections(this.token);
        this.collections = response.map((item) => ({
          id: item.id,
          name: item.name,
          status: item.status,
        }));
      } catch (error) {
        console.error('Erro ao obter coleções:', error);
      }
    },
    async loadVideotecaStatus() {
      try {
        const status = await companyApi.getVideoteca(this.token);
        this.videotecaFolderId =
          status?.folderId
          || status?.idFolder
          || status?.idFolderVideoteca
          || null;
      } catch (error) {
        console.warn('Não foi possível carregar status da Videoteca:', error);
      }
    },
  },

  created() {
    this.getCollections();
    this.loadVideotecaStatus();
  },
};
</script>

<style scoped>
@import '@/css/weeduc/admin/createVideo.css';
</style>
